/**
 * SULYALAPU SZALLITASI DIJSZABAS.
 *
 * ────────────────────────────────────────────────────────────────────
 * FORRAS ES ELLENORZES
 *
 * Forrasfajl: `kvikk-arlista-2026-08-01-2026-08-31-4.pdf`, "Belfoldi
 * szallitasi dijak" tablazat. A PDF fejlecben: "Az arak nettoban ertendok,
 * az AFA-t nem tartalmazzak."
 *
 * A PDF 12 oszlopa es a mi `TariffColumn` azonositoink:
 *
 *   MPL hazhoz .................. mpl_home
 *   MPL Postan marado ........... mpl_posta        (a brief "MPL posta" oszlopa)
 *   MPL Postapont ............... mpl_postapont
 *   MPL Automata ................ mpl_automata
 *   Foxpost hazhoz .............. NINCS ATVEVE - hazhozszallitasra csak
 *                                 GLS / MPL / DPD valaszthato
 *   Foxpost, Z-Pont, Z-Box ...... locker_flat      (savtol fuggetlenul 800 Ft)
 *   DPD hazhoz .................. dpd_home
 *   DPD Csomagpont, Alzabox ..... dpd_point
 *   GLS hazhoz .................. gls_home
 *   GLS CsomagPont .............. gls_shop
 *   GLS Automata ................ gls_locker
 *   FamaFutar hazhoz ............ NINCS ATVEVE - a FamaFutar semmilyen
 *                                 aktiv valasztoban nem szerepelhet
 *
 * A `null` azt jelenti, hogy az adott szolgaltatas abban a sulysavban nem
 * valaszthato (a PDF-ben ures cella). Ilyenkor a hivo `unavailable`
 * eredmenyt kap, es a felulet elrejti a lehetoseget - nem szamolunk
 * helyette "legkozelebbi" dijat.
 * ────────────────────────────────────────────────────────────────────
 *
 * AZ UGYFEL EGYETLEN "Szallitas" OSSZEGET LAT. A nettó tarifa, a 27%-os
 * AFA-felszorzas es az MPL felveteli dij KULON-KULON belso adat: sem a
 * publikus ajanlati oldalon, sem a publikus RPC valaszaban nem jelenhet
 * meg kulon tetelkent.
 */

// ─────────────────────────── azonositok ───────────────────────────

/** Az AKTIV futarszolgalatok. FamaFutar SZANDEKOSAN nincs a listaban. */
export const ACTIVE_COURIERS = ['mpl', 'foxpost', 'packeta', 'dpd', 'gls'] as const
export type ActiveCourier = (typeof ACTIVE_COURIERS)[number]

/** Hazhozszallitasra CSAK ez a harom valaszthato. */
export const HOME_COURIERS = ['gls', 'mpl', 'dpd'] as const
export type HomeCourier = (typeof HOME_COURIERS)[number]

/**
 * Csomagpont-tipusok futarszolgalatonkent, a Kvikk `deliveryPointType`
 * ertekeivel egyezoen. Csak magyarorszagi pontok.
 */
export const POINT_TYPES = {
  mpl: ['posta', 'postapont', 'automata'],
  foxpost: ['foxpost'],
  packeta: ['zpont', 'zbox'],
  dpd: ['parcelshop', 'alzabox'],
  gls: ['shop', 'locker']
} as const satisfies Record<ActiveCourier, readonly string[]>

export type PointType = (typeof POINT_TYPES)[ActiveCourier][number]

/** Egy tarifaoszlop a PDF tablazatabol. */
export type TariffColumn =
  | 'mpl_home'
  | 'mpl_posta'
  | 'mpl_postapont'
  | 'mpl_automata'
  | 'locker_flat'
  | 'dpd_home'
  | 'dpd_point'
  | 'gls_home'
  | 'gls_shop'
  | 'gls_locker'

// ─────────────────────────── a rate card ───────────────────────────

export interface RateBand {
  /** A sav FELSO hatara grammban, BELEERTVE. */
  maxGrams: number
  /** `null` = az adott szolgaltatas ebben a savban nem valaszthato. */
  rates: Record<TariffColumn, number | null>
}

export interface ShippingRateCard {
  /** Verzioazonosito. Ez kerul az ajanlatra es a rendelesre. */
  version: string
  /** Ervenyesseg (befoglaló, ISO datum). */
  validFrom: string
  validUntil: string
  /** A `bands` ertekei nettok VAGY bruttok. A PDF nettot ad. */
  priceBasis: 'net' | 'gross'
  source: string
  /**
   * AFA-kulcs. A tarifa nettoban erkezik, az ugyfelnek felszamitott osszeg
   * viszont brutto - VOXORA alanyi adomentes, tehat az afat nem tudja
   * levonni, az a valodi koltseg resze.
   */
  vatRate: number
  /**
   * Az MPL felveteli dijra IS vonatkozik-e az afa.
   *
   * UZLETI DONTES (jovahagyva): igen. A keplet ezert
   * `kerek5((tarifa + felveteli_dij) * (1 + afa))`.
   */
  vatAppliesToPickupFee: boolean
  /** Az ugyfelnek mutatott osszeg kerekitesi lepese forintban. */
  roundToHuf: number
  /**
   * Minden MPL-kuldemenynel hozzaadott fix osszeg.
   *
   * KULON TETELKENT SOSEM JELENIK MEG AZ UGYFELNEK. Belso mezoben
   * tarolodik, hogy az elszamolasban visszakovetheto legyen.
   */
  mplPickupFeeHuf: number
  /**
   * A CSOMAGOLAS tomege grammban - doboz, buborekfolia, toltoanyag.
   *
   * ADMINISZTRALHATO: egyetlen szam, amit a rate card uj verziojaval
   * lehet modositani. A tarifasav valasztasakor a modellek tomegehez
   * mindig hozzaadodik.
   */
  packagingTareG: number
  bands: RateBand[]
}

/*
  ────────────────────────────────────────────────────────────────────
  A SZAMOK A PDF-BOL, SORRENDBEN.

  Ellenorizve a `kvikk-arlista-2026-08-01-2026-08-31-4.pdf` 1. oldalanak
  "Belfoldi szallitasi dijak" tablazata alapjan, soronkent.

  Ha uj arlista erkezik: NE ird at ezt a tombot. Vegy fel egy UJ verziot,
  sajat ervenyessegi datummal - a mar kiadott ajanlatok szamai kulonben
  visszamenoleg megvaltoznanak.
  ────────────────────────────────────────────────────────────────────
*/
export const RATE_CARD_2026_08: ShippingRateCard = {
  version: '2026-08',
  validFrom: '2026-08-01',
  validUntil: '2026-08-31',
  priceBasis: 'net',
  source: 'kvikk-arlista-2026-08-01-2026-08-31-4.pdf / Belföldi szállítási díjak',
  vatRate: 0.27,
  vatAppliesToPickupFee: true,
  roundToHuf: 5,
  mplPickupFeeHuf: 480,
  packagingTareG: 150,
  bands: [
    {
      maxGrams: 1000,
      rates: { mpl_home: 1130, mpl_posta: 1040, mpl_postapont: 1080, mpl_automata: 860, locker_flat: 800, dpd_home: 1310, dpd_point: 880, gls_home: 1540, gls_shop: 1230, gls_locker: 1130 }
    },
    {
      maxGrams: 2000,
      rates: { mpl_home: 1260, mpl_posta: 1060, mpl_postapont: 1110, mpl_automata: 880, locker_flat: 800, dpd_home: 1310, dpd_point: 880, gls_home: 1550, gls_shop: 1240, gls_locker: 1140 }
    },
    {
      maxGrams: 3000,
      rates: { mpl_home: 1310, mpl_posta: 1110, mpl_postapont: 1160, mpl_automata: 890, locker_flat: 800, dpd_home: 1310, dpd_point: 880, gls_home: 1690, gls_shop: 1370, gls_locker: 1270 }
    },
    {
      maxGrams: 4000,
      rates: { mpl_home: 1360, mpl_posta: 1140, mpl_postapont: 1200, mpl_automata: 900, locker_flat: 800, dpd_home: 1540, dpd_point: 990, gls_home: 1890, gls_shop: 1580, gls_locker: 1470 }
    },
    {
      maxGrams: 5000,
      rates: { mpl_home: 1450, mpl_posta: 1180, mpl_postapont: 1240, mpl_automata: 910, locker_flat: 800, dpd_home: 1540, dpd_point: 990, gls_home: 1910, gls_shop: 1590, gls_locker: 1490 }
    },
    {
      maxGrams: 10000,
      rates: { mpl_home: 1520, mpl_posta: 1240, mpl_postapont: 1300, mpl_automata: 960, locker_flat: 800, dpd_home: 1540, dpd_point: 990, gls_home: 2200, gls_shop: 1900, gls_locker: 1780 }
    },
    {
      maxGrams: 15000,
      rates: { mpl_home: 1780, mpl_posta: 1420, mpl_postapont: 1490, mpl_automata: 1090, locker_flat: 800, dpd_home: 1780, dpd_point: 1670, gls_home: 2460, gls_shop: 2110, gls_locker: 2040 }
    },
    {
      maxGrams: 20000,
      rates: { mpl_home: 1940, mpl_posta: 1560, mpl_postapont: 1640, mpl_automata: 1230, locker_flat: 800, dpd_home: 1880, dpd_point: 1670, gls_home: 2750, gls_shop: 2480, gls_locker: 2340 }
    },
    {
      maxGrams: 25000,
      rates: { mpl_home: 2280, mpl_posta: 1830, mpl_postapont: null, mpl_automata: null, locker_flat: 800, dpd_home: 2290, dpd_point: null, gls_home: 3010, gls_shop: 2650, gls_locker: 2590 }
    },
    {
      maxGrams: 30000,
      rates: { mpl_home: 2610, mpl_posta: 2090, mpl_postapont: null, mpl_automata: null, locker_flat: null, dpd_home: 2290, dpd_point: null, gls_home: 3120, gls_shop: 2870, gls_locker: 2700 }
    },
    {
      maxGrams: 35000,
      rates: { mpl_home: 3970, mpl_posta: null, mpl_postapont: null, mpl_automata: null, locker_flat: null, dpd_home: null, dpd_point: null, gls_home: 3780, gls_shop: 3390, gls_locker: 3360 }
    },
    {
      maxGrams: 40000,
      rates: { mpl_home: 4220, mpl_posta: null, mpl_postapont: null, mpl_automata: null, locker_flat: null, dpd_home: null, dpd_point: null, gls_home: 3760, gls_shop: 3530, gls_locker: 3350 }
    }
  ]
}

/** Az aktualisan hasznalt rate card. */
export const ACTIVE_RATE_CARD = RATE_CARD_2026_08

/** Verziolista, hogy egy regi ajanlat szamai visszakereshetok legyenek. */
export const RATE_CARDS: Record<string, ShippingRateCard> = {
  [RATE_CARD_2026_08.version]: RATE_CARD_2026_08
}

export const rateCardByVersion = (version: string): ShippingRateCard | undefined =>
  RATE_CARDS[version]

// ─────────────────────── szolgaltatas -> oszlop ───────────────────────

/**
 * Egy konkret atveteli mod. Ebbol lesz a tarifaoszlop.
 *
 * `personal` eseten nincs futar es nincs dij - a `kind` ezert kulon
 * ertek, nem egy "0 Ft-os futar".
 */
export type ShippingService =
  | { kind: 'personal' }
  | { kind: 'home'; courier: HomeCourier }
  | { kind: 'parcel_point'; courier: ActiveCourier; pointType: PointType }

const HOME_COLUMN: Record<HomeCourier, TariffColumn> = {
  mpl: 'mpl_home',
  dpd: 'dpd_home',
  gls: 'gls_home'
}

const POINT_COLUMN: Record<string, TariffColumn> = {
  'mpl:posta': 'mpl_posta',
  'mpl:postapont': 'mpl_postapont',
  'mpl:automata': 'mpl_automata',
  // A Foxpost es a Packeta (Z-Pont, Z-Box) ugyanabban az oszlopban van:
  // a PDF egyetlen "Foxpost, Z-Pont, Z-Box" oszlopot ad, savtol fuggetlen
  // 800 Ft-tal.
  'foxpost:foxpost': 'locker_flat',
  'packeta:zpont': 'locker_flat',
  'packeta:zbox': 'locker_flat',
  'dpd:parcelshop': 'dpd_point',
  'dpd:alzabox': 'dpd_point',
  'gls:shop': 'gls_shop',
  'gls:locker': 'gls_locker'
}

/** `undefined`, ha a parositas nem letezik (pl. rossz ponttipus). */
export function tariffColumnFor(service: ShippingService): TariffColumn | undefined {
  if (service.kind === 'personal') return undefined
  if (service.kind === 'home') return HOME_COLUMN[service.courier]
  return POINT_COLUMN[`${service.courier}:${service.pointType}`]
}

/** A ponttipus valoban letezik-e az adott futarnal. */
export function isAllowedPointType(courier: string, pointType: string): boolean {
  const list = (POINT_TYPES as Record<string, readonly string[]>)[courier]
  return Boolean(list?.includes(pointType))
}

// ─────────────────────────── szamitas ───────────────────────────

/** Magyar kerekites a legkozelebbi lepeskozre. */
export function roundToStep(value: number, step: number): number {
  return step <= 0 ? Math.round(value) : Math.round(value / step) * step
}

/**
 * A tarifahoz hasznalt tomeg.
 *
 * A modellek becsult (vagy szeletelt) tomegehez hozzaadjuk a csomagolast,
 * majd EGESZ GRAMMRA FELFELE kerekitunk. A felfele kerekites SZANDEKOS: egy
 * 1000,4 grammos kuldemeny a futarnal mar a kovetkezo savba esik.
 */
export function billableGrams(modelGrams: number, card = ACTIVE_RATE_CARD): number {
  return Math.ceil(Math.max(0, modelGrams) + card.packagingTareG)
}

export function bandFor(grams: number, card = ACTIVE_RATE_CARD): RateBand | undefined {
  return card.bands.find((b) => grams <= b.maxGrams)
}

export type ShippingUnavailableReason =
  | 'over_max_weight'
  | 'service_not_in_band'
  | 'unknown_service'

export interface ShippingCalculation {
  available: true
  /** Melyik rate card verzio adta az eredmenyt. */
  rateCardVersion: string
  /** A tarifahoz hasznalt, felfele kerekitett tomeg grammban. */
  billableGrams: number
  packagingTareG: number
  bandMaxGrams: number
  column: TariffColumn
  /** BELSO: a rate card nyers, nettó erteke. */
  internalBaseNetHuf: number
  /** BELSO: az MPL felveteli dij (0, ha nem MPL). */
  internalMplPickupNetHuf: number
  /** BELSO: amibe a kuldemeny valojaban kerul, afaval, kerekitve. */
  internalCostGrossHuf: number
  /** AMIT AZ UGYFEL LAT - egyetlen osszevont osszeg. 0, ha ingyenes. */
  customerShippingHuf: number
  /** Ingyenes szallitas lepett-e eletbe. */
  freeShipping: boolean
}

export interface ShippingUnavailable {
  available: false
  reason: ShippingUnavailableReason
  billableGrams: number
  rateCardVersion: string
  message: string
}

export type ShippingResult = ShippingCalculation | ShippingUnavailable

/**
 * A SZALLITASI DIJ.
 *
 *   1. tomeg = felfele kerekit(modellek + csomagolas)
 *   2. sav   = az elso sav, amibe a tomeg belefer
 *   3. nettó = savbeli tarifa + (MPL ? felveteli dij : 0)
 *   4. brutto = kerekit5(nettó * (1 + afa))
 *   5. ingyenes, ha a gyartasi reszosszeg SZIGORUAN 15 000 Ft folott van
 *
 * A `productionSubtotalHuf` a gyartas + minimum + Expressz felar, szallitas
 * NELKUL. Ha nincs megadva, nem szamolunk ingyenes szallitast.
 */
export const FREE_SHIPPING_ABOVE_HUF = 15_000

export function calculateShipping(
  input: {
    service: ShippingService
    /** A modellek osszes tomege grammban (becsult vagy szeletelt). */
    modelGrams: number
    /** Gyartas + minimum + Expressz, szallitas nelkul. */
    productionSubtotalHuf?: number
  },
  card: ShippingRateCard = ACTIVE_RATE_CARD
): ShippingResult {
  const grams = billableGrams(input.modelGrams, card)

  // Szemelyes atvetel: nincs futar, nincs dij, nincs sulyhatar.
  if (input.service.kind === 'personal') {
    return {
      available: true,
      rateCardVersion: card.version,
      billableGrams: grams,
      packagingTareG: card.packagingTareG,
      bandMaxGrams: 0,
      column: 'mpl_home',
      internalBaseNetHuf: 0,
      internalMplPickupNetHuf: 0,
      internalCostGrossHuf: 0,
      customerShippingHuf: 0,
      freeShipping: false
    }
  }

  const column = tariffColumnFor(input.service)
  if (!column) {
    return {
      available: false,
      reason: 'unknown_service',
      billableGrams: grams,
      rateCardVersion: card.version,
      message: 'Ez az átvételi mód nem választható.'
    }
  }

  const band = bandFor(grams, card)
  if (!band) {
    return {
      available: false,
      reason: 'over_max_weight',
      billableGrams: grams,
      rateCardVersion: card.version,
      message:
        'A csomag súlya meghaladja a futárszolgálati felső határt (40 kg). ' +
        'Írj nekünk, és megkeressük a legjobb megoldást.'
    }
  }

  const base = band.rates[column]
  if (base == null) {
    return {
      available: false,
      reason: 'service_not_in_band',
      billableGrams: grams,
      rateCardVersion: card.version,
      message: 'Ez az átvételi mód ilyen súlyú csomagnál nem választható.'
    }
  }

  const pickup = isMplColumn(column) ? card.mplPickupFeeHuf : 0
  const vatable = card.vatAppliesToPickupFee ? base + pickup : base
  const flat = card.vatAppliesToPickupFee ? 0 : pickup
  const gross = roundToStep(vatable * (1 + card.vatRate) + flat, card.roundToHuf)

  const freeShipping =
    input.productionSubtotalHuf != null &&
    input.productionSubtotalHuf > FREE_SHIPPING_ABOVE_HUF

  return {
    available: true,
    rateCardVersion: card.version,
    billableGrams: grams,
    packagingTareG: card.packagingTareG,
    bandMaxGrams: band.maxGrams,
    column,
    internalBaseNetHuf: base,
    internalMplPickupNetHuf: pickup,
    internalCostGrossHuf: gross,
    customerShippingHuf: freeShipping ? 0 : gross,
    freeShipping
  }
}

const isMplColumn = (column: TariffColumn): boolean => column.startsWith('mpl_')

/**
 * Mely szolgaltatasok valaszthatok EBBEN a sulysavban.
 *
 * A felulet ezzel rejti el azokat a lehetosegeket, amelyek az adott
 * csomagsulynal nem elerhetok - igy a vasarlo nem valaszt olyat, amit a
 * vegen visszautasitanank.
 */
export function availableServices(
  modelGrams: number,
  card: ShippingRateCard = ACTIVE_RATE_CARD
): { home: HomeCourier[]; points: Array<{ courier: ActiveCourier; pointType: PointType }> } {
  const grams = billableGrams(modelGrams, card)
  const band = bandFor(grams, card)
  if (!band) return { home: [], points: [] }

  const home = HOME_COURIERS.filter((c) => band.rates[HOME_COLUMN[c]] != null)

  const points: Array<{ courier: ActiveCourier; pointType: PointType }> = []
  for (const courier of ACTIVE_COURIERS) {
    for (const pointType of POINT_TYPES[courier]) {
      const column = POINT_COLUMN[`${courier}:${pointType}`]
      if (column && band.rates[column] != null) {
        points.push({ courier, pointType: pointType as PointType })
      }
    }
  }

  return { home: [...home], points }
}
